import { supabase } from '@/integrations/supabase/client';

export interface GiftCardRow {
  id: string;
  shopify_gift_card_gid: string | null;
  last_characters: string | null;
  initial_amount: number;
  balance_amount: number | null;
  currency: string;
  status: string;
  expires_on: string | null;
  recipient_name: string;
  recipient_email: string | null;
  recipient_phone: string | null;
  sender_name: string | null;
  message: string | null;
  internal_note?: string | null;
  occasion: string | null;
  template: string | null;
  source: string;
  issue_reason: string | null;
  campaign_id: string | null;
  deliver_at: string | null;
  sent_at: string | null;
  opened_at: string | null;
  balance_synced_at: string | null;
  created_at: string;
}

export interface GiftCardSettings {
  id: string;
  public_sale_enabled: boolean;
  presets_ils: number[];
  min_amount_ils: number;
  max_amount_ils: number;
  allow_custom_amount: boolean;
  expiry_months: number | null;
  occasions: string[];
  templates: unknown[];
  logo_url: string | null;
  bg_style: string;
  accent: string;
  headline_he: string;
  terms_he: string;
  sender_name: string;
  reply_to: string | null;
  gift_product_variant_gid: string | null;
}

export interface GiftHealth {
  shopifyGiftCards: {
    read: boolean;
    write: boolean;
    read_transactions: boolean;
    write_transactions: boolean;
    granted?: string[];
    message: string | null;
  };
  encryption: boolean;
  email: boolean;
  emailProvider?: 'google_workspace' | 'resend' | 'manual';
  emailSender?: string | null;

  pepper?: boolean;
  publicSiteUrl?: boolean;
  publicRevealEnabled?: boolean;
  /** Stage 5: readiness of the public purchase + webhook pipeline. */
  purchase?: {
    storefront: boolean;
    variant: boolean;
    webhook: boolean;
    ready: boolean;
    publicSaleEnabled: boolean;
    blockers: string[];
  };
  /** Stage 6: live Shopify webhook registration (no secret values). */
  webhooks?: {
    topics: string[];
    missing: string[];
    callbackUrl: string;
    ok: boolean;
    checked: boolean;
    message: string | null;
  };
  /** Stage 6: scheduled maintenance job status. */
  automation?: {
    cronConfigured: boolean;
    lastSyncAt: string | null;
    lastSyncStatus: string | null;
    syncFresh: boolean;
  };
}


export type GiftTransactionStatus = 'processing' | 'succeeded' | 'failed' | 'needs_reconciliation';

export interface GiftTransaction {
  status: GiftTransactionStatus;
  request_hash: string | null;
  failure_reason: string | null;
  id: string;
  gift_card_id: string;
  type: 'debit' | 'credit';
  amount: number;
  currency: string;
  shopify_transaction_gid: string | null;
  appointment_id: string | null;
  client_id: string | null;
  service_reference: string | null;
  shopify_product_reference: string | null;
  reason: string | null;
  note: string | null;
  balance_before: number | null;
  balance_after: number | null;
  reversed_transaction_id: string | null;
  actor_user_id: string | null;
  processed_at: string | null;
  created_at: string;
}

export interface GiftCampaign {
  id: string;
  kind: string;
  status: string;
  amount_default: number | null;
  message_default: string | null;
  occasion: string | null;
  sender_name: string | null;
  deliver_at: string | null;
  total_recipients: number;
  created_at: string;
}

export interface GiftRecipientRow {
  id: string;
  campaign_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  amount: number;
  message: string | null;
  send_at: string | null;
  status: string;
  error: string | null;
  gift_card_id: string | null;
}

export interface GiftEvent {
  id: string;
  type: string;
  actor: string | null;
  meta: Record<string, unknown>;
  created_at: string;
}

/**
 * Error thrown by {@link giftAdmin}. `keepKey` tells the caller whether the
 * operation may still exist server-side, in which case the SAME idempotency key
 * must be reused instead of starting a new issuance.
 */
export class GiftAdminError extends Error {
  readonly status?: string;
  readonly keepKey: boolean;
  readonly operationId?: string;

  constructor(message: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'GiftAdminError';
    this.status = typeof body?.status === 'string' ? body.status : undefined;
    // Unknown outcomes (network/timeout) are treated as "keep the key".
    this.keepKey = body?.keepKey === true || body === undefined;
    this.operationId = typeof body?.operation_id === 'string' ? body.operation_id : undefined;
  }
}

/** Calls the admin gift-card function. Throws with the server message on failure. */
export async function giftAdmin<T = any>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('gift-cards-admin', {
    body: { action, ...payload },
  });
  if (error) {
    let details = error.message;
    let body: Record<string, unknown> | undefined;
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.text === 'function') {
        const text = await ctx.text();
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object') body = parsed;
        if (parsed?.error) details = parsed.error;
      }
    } catch {
      /* keep the generic message */
    }
    throw new GiftAdminError(details, body);
  }
  if (data && typeof data === 'object' && 'error' in data && (data as any).error) {
    throw new GiftAdminError(String((data as any).error), data as Record<string, unknown>);
  }
  return data as T;
}


export async function giftPublicConfig(): Promise<Partial<GiftCardSettings> & { purchase_enabled: boolean }> {
  const { data, error } = await supabase.functions.invoke('gift-card-public', { body: {} });
  if (error) throw new Error(error.message);
  if ((data as any)?.error) throw new Error((data as any).error);
  return (data as any).config;
}

export interface GiftPurchaseRequest {
  amount: number;
  recipient_name: string;
  recipient_email: string;
  sender_name?: string | null;
  message?: string | null;
  occasion?: string | null;
  deliver_at?: string | null;
}

/**
 * Creates a SERVER-OWNED purchase intent and returns the Shopify checkout URL.
 * The browser never states the authoritative amount: the server re-derives it
 * from settings + the live variant price and signs the cart line itself.
 * A redirect to checkout is NOT proof of payment — issuance happens only after
 * the verified orders/paid webhook.
 */
export async function giftPurchaseIntent(
  payload: GiftPurchaseRequest,
): Promise<{ intent_id: string; checkout_url: string; amount: number }> {
  const { data, error } = await supabase.functions.invoke('gift-card-purchase', {
    body: { action: 'intent.create', ...payload },
  });
  if (error) throw new Error(await readInvokeError(error));
  if ((data as any)?.error) throw new Error((data as any).error);
  return data as { intent_id: string; checkout_url: string; amount: number };
}


/**
 * Public presentation payload for /gift/:token. Deliberately excludes the full
 * code, ids, hashes, the Shopify GID and every personal contact detail.
 */
export interface PublicGiftCard {
  last_characters: string | null;
  initial_amount: number;
  /** Authoritative Shopify balance, or null when it could not be refreshed. */
  balance_amount: number | null;
  /** False => show "balance temporarily unavailable", never the initial amount. */
  balance_available: boolean;
  balance_synced_at: string | null;
  currency: string;
  status: string;
  expires_on: string | null;
  recipient_name: string;
  sender_name: string | null;
  message: string | null;
  occasion: string | null;
  template: string | null;
}

export async function giftView(token: string): Promise<{
  card: PublicGiftCard;
  design: { headline_he: string; terms_he: string; logo_url: string | null; bg_style: string; accent: string } | null;
  /** Canonical public page URL (server-owned origin) used for the QR code. */
  public_url?: string | null;
}> {
  const { data, error } = await supabase.functions.invoke('gift-card-view', { body: { token } });
  if (error) throw new Error(await readInvokeError(error));
  if ((data as any)?.error) throw new Error((data as any).error);
  return data as any;
}

// ===================== OTP-protected one-time reveal =====================

/**
 * otp.request always answers 200 with this exact, generic shape — for a real
 * card, an unknown link, a cooldown, a lock, a rate limit or an internal
 * failure. Throttle state is never exposed at the request stage.
 */
export interface OtpRequestResult {
  accepted: true;
  state: 'accepted';
  challenge_id: string;
  resend_after_seconds: number;
  expires_in_seconds: number;
}


export interface OtpVerifyResult {
  verified: boolean;
  state: string;
  reveal_id?: string;
  reveal_token?: string;
  attempts_left?: number;
  retry_after_seconds?: number;
}

/**
 * Calls the OTP endpoint. The raw response body is returned even for 4xx
 * statuses, because throttle/attempt state is part of the normal UX.
 */
async function otpCall<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('gift-card-otp', {
    body: { action, ...payload },
  });
  if (error) {
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.text === 'function') {
        const parsed = JSON.parse(await ctx.text());
        if (parsed && typeof parsed === 'object') return parsed as T;
      }
    } catch {
      /* fall through to a generic failure */
    }
    throw new Error('שגיאה זמנית. נסו שוב.');
  }
  return data as T;
}

/** Step 1 — always resolves generically; never reveals whether a mail was sent. */
export async function giftOtpRequest(token: string): Promise<OtpRequestResult> {
  return await otpCall<OtpRequestResult>('otp.request', { token });
}

/** Step 2 — returns a single-use reveal capability, never the card code. */
export async function giftOtpVerify(token: string, challengeId: string, code: string): Promise<OtpVerifyResult> {
  return await otpCall<OtpVerifyResult>('otp.verify', { token, challenge_id: challengeId, code });
}

/** Step 3 — consumes the capability once and returns the full code exactly once. */
export async function giftRevealConsume(
  token: string,
  revealId: string,
  revealToken: string,
): Promise<{ code: string; last_characters: string | null }> {
  const res = await otpCall<{ code?: string; error?: string }>('reveal.consume', {
    token,
    reveal_id: revealId,
    reveal_token: revealToken,
  });
  if (!res.code) throw new Error('הקוד אינו זמין יותר. בקשו קוד אימות חדש.');
  return res as { code: string; last_characters: string | null };
}


async function readInvokeError(error: any): Promise<string> {
  try {
    if (error?.context && typeof error.context.text === 'function') {
      const parsed = JSON.parse(await error.context.text());
      if (parsed?.error) return parsed.error;
    }
  } catch {
    /* fall through */
  }
  return error?.message ?? 'שגיאה';
}

export const GIFT_STATUS_LABELS: Record<string, string> = {
  pending_payment: 'ממתין לתשלום',
  scheduled: 'מתוזמן',
  sent: 'נשלח',
  opened: 'נצפה',
  active: 'פעיל',
  partially_used: 'נוצל חלקית',
  depleted: 'נוצל',
  expired: 'פג תוקף',
  revoked: 'בוטל',
  failed: 'שליחה נכשלה',
};

export const GIFT_REASONS: Record<string, string> = {
  complimentary: 'מחמאה / מתנה',
  compensation: 'פיצוי',
  promotion: 'קמפיין שיווקי',
  manual_sale: 'מכירה ידנית',
};

export function formatIls(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(value)} ₪`;
}

// ===================== Stage 5: site purchases + reconciliation =====================

export interface GiftPurchaseIntentRow {
  id: string;
  status: string;
  amount: number;
  currency: string;
  quantity: number;
  unit_price: number;
  recipient_name: string;
  recipient_email: string | null;
  sender_name: string | null;
  occasion: string | null;
  deliver_at: string | null;
  order_gid: string | null;
  order_name: string | null;
  paid_amount: number | null;
  paid_at: string | null;
  gift_card_id: string | null;
  issued_at: string | null;
  failure_code: string | null;
  reconciliation_reason: string | null;
  created_at: string;
}

export interface GiftAlertRow {
  id: string;
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  status: 'open' | 'acknowledged' | 'resolved';
  gift_card_id: string | null;
  intent_id: string | null;
  campaign_id: string | null;
  order_gid: string | null;
  detail: Record<string, unknown>;
  /** Deduplicated repeat count for the same open alert identity. */
  occurrences?: number;
  last_seen_at?: string | null;
  created_at: string;
}

export interface GiftWebhookEventRow {
  id: string;
  topic: string;
  status: string;
  outcome: string | null;
  error_code: string | null;
  attempts: number;
  created_at: string;
  processed_at: string | null;
}

export const GIFT_PURCHASE_STATUS_LABELS: Record<string, string> = {
  created: 'נפתח',
  checkout_started: 'ממתין לתשלום',
  paid: 'שולם',
  issuing: 'בהנפקה',
  issued: 'הונפק',
  rejected: 'נדחה',
  needs_reconciliation: 'דרושה בדיקה',
  expired: 'פג',
};

export const GIFT_ALERT_LABELS: Record<string, string> = {
  paid_order_without_intent: 'תשלום ללא רכישה מזוהה',
  paid_amount_mismatch: 'אי-התאמה בסכום ששולם',
  paid_currency_mismatch: 'אי-התאמה במטבע',
  paid_variant_mismatch: 'מוצר שאינו שובר',
  paid_quantity_mismatch: 'אי-התאמה בכמות',
  paid_discount_present: 'הנחה לא צפויה על השובר',
  paid_intent_not_payable: 'רכישה במצב לא צפוי',
  issuance_uncertain: 'הנפקה במצב לא ודאי',
  paid_but_not_issued: 'שולם אך לא הונפק',
  intent_mark_paid_failed: 'רישום התשלום נכשל',
  refund_partially_used_card: 'החזר על שובר שנוצל חלקית',
  refund_unused_card: 'החזר על שובר שלא נוצל',
  refund_without_card: 'החזר ללא שובר',
  campaign_row_uncertain: 'שורת קמפיין במצב לא ודאי',
  delivery_outcome_unknown: 'תוצאת שליחת המייל אינה ודאית — לא נשלח שוב אוטומטית',
  webhook_stale_processing: 'עיבוד הזמנה נתקע — נדרשת בדיקה ידנית בשופיפיי',
};
